import React, { useEffect, useState } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Typography, Box, Grid, IconButton
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventIcon from '@mui/icons-material/Event';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { headers } from "../../../utils/utils";

// Format date & time in Indian format
const formatDateTime = (dateTimeStr) => {
    const utcDate = new Date(dateTimeStr + 'Z');
    return utcDate.toLocaleString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
    });
};

// Format seconds as hh:mm:ss
const formatDuration = (seconds) => {
    const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
    const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
};

const WorkTimerTable = ({ ticketId, memberList }) => {
    const [sessions, setSessions] = useState([]);
    const [showTable, setShowTable] = useState(false); // toggle visibility

    useEffect(() => {
        if (!ticketId) return;
        if (showTable) {
            fetch(`${process.env.REACT_APP_TICKET_SERVER}/ticket/timeSheet/${ticketId}`, {
                method: 'GET',
                headers: headers()
            })
                .then(res => res.json())
                .then(data => {
                    setSessions(data);
                })
                .catch(err => {
                    console.error('Failed to load work sessions:', err);
                });
        }
    }, [showTable]);

    if (!ticketId) {
        return <Typography>Ticket ID is missing</Typography>;
    }

    return (
        <Grid item xs={12} sm={7.8} sx={{ ml: 2 }}>
            {/* Clickable Header */}
            <Box
                sx={{ display: "flex", alignItems: "center", mt: 2, cursor: "pointer" }}
                onClick={() => setShowTable(!showTable)}
            >
                <AccessTimeIcon />
                <Typography
                    component="div"
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1, fontWeight: 'bold' }}
                >
                    Time Sheet
                </Typography>
                <IconButton size="small" sx={{ ml: 1 }}>
                    {showTable ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>

            {/* Conditionally Render Table */}
            {showTable && (
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">
                                    <EventIcon sx={{ verticalAlign: 'middle', mr: 1 }} /> Date
                                </TableCell>
                                <TableCell align="center">
                                    <PlayCircleOutlineIcon color="success" sx={{ verticalAlign: 'middle', mr: 1 }} /> Start Time
                                </TableCell>
                                <TableCell align="center">
                                    <StopCircleIcon color="error" sx={{ verticalAlign: 'middle', mr: 1 }} /> End Time
                                </TableCell>
                                <TableCell align="center">
                                    <AccessTimeIcon color="primary" sx={{ verticalAlign: 'middle', mr: 1 }} /> Duration
                                </TableCell>
                                <TableCell align="center">
                                    <PersonIcon color="secondary" sx={{ verticalAlign: 'middle', mr: 1 }} /> Member
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sessions.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        No work sessions found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                [...sessions]
                                    .sort((a, b) => new Date(b.startTime) - new Date(a.startTime))
                                    // Descending order
                                    .map((session) => {
                                        const startDate = new Date(session.startTime + 'Z');
                                        const dateStr = startDate.toLocaleDateString('en-IN');
                                        const startTimeStr = startDate.toLocaleTimeString('en-IN');
                                        const endDate = session.endTime ? new Date(session.endTime + 'Z') : null;
                                        const endTimeStr = endDate ? endDate.toLocaleTimeString('en-IN') : 'In Progress';
                                        const durationStr = session.elapsedSeconds ? formatDuration(session.elapsedSeconds) : 'N/A';
                                        const userName = memberList?.find(m => m.id === session.userId)?.fullName || session.userId;

                                        return (
                                            <TableRow key={session.id} hover>
                                                <TableCell align="center" sx={{ color: '#388e3c' }}>{dateStr}</TableCell>
                                                <TableCell align="center" sx={{ color: '#00796b' }}>{startTimeStr}</TableCell>
                                                <TableCell align="center" sx={{ color: session.endTime ? '#d32f2f' : '#ff9800' }}>{endTimeStr}</TableCell>
                                                <TableCell align="center" sx={{ color: '#1976d2' }}>{durationStr}</TableCell>
                                                <TableCell align="center" sx={{ color: '#6a1b9a' }}>{userName}</TableCell>
                                            </TableRow>
                                        );
                                    })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Grid>
    );
};

export default WorkTimerTable;
